import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import '../styles/CaseStudyDetail.css';

const BCDRCaseStudy = () => {
  const navigate = useNavigate();

  const handleBackToPortfolio = () => {
    navigate('/', { state: { scrollTo: 'work' } });
  };

  // Hide header on scroll down, show on scroll up
  useEffect(() => {
    let lastScrollY = window.scrollY;
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const header = document.querySelector('.detail-header');
      
      if (!header) return;
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down & past threshold
        header.classList.add('hidden');
      } else {
        // Scrolling up
        header.classList.remove('hidden');
      }
      
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="case-study-detail">
      {/* Header */}
      <header className="detail-header">
        <div className="detail-header-content">
          <button onClick={handleBackToPortfolio} className="back-button">
            <ArrowLeft size={20} />
            <span>Go back</span>
          </button>
        </div>
      </header>

      {/* Hero Video */}
      <section className="detail-hero">
        <video 
          src="https://customer-assets.emergentagent.com/job_portfolio-resume-28/artifacts/y191dcei_Screen%20Recording%202026-04-08%20at%206.13.33%E2%80%AFAM.mov"
          className="hero-video"
          autoPlay
          loop
          muted
          playsInline
        />
      </section>

      {/* Content */}
      <section className="detail-content">
        <div className="content-container">
          
          <h1 className="project-title">Real-Time Decision Dashboard</h1>
          <p className="project-subtitle">Turning passive reports into an active decision system. Leaders went from waiting days for static snapshots to monitoring live data and making immediate decisions.</p>

          {/* Metrics */}
          <div className="metrics-section metrics-compact">
            <div className="metric-card-compact">
              <span className="metric-value-compact">24+ hours saved monthly</span>
            </div>
            <div className="metric-card-compact">
              <span className="metric-value-compact">Real-time vs 2-3 day delays</span>
            </div>
            <div className="metric-card-compact">
              <span className="metric-value-compact">100% unified data view</span>
            </div>
          </div>

          {/* Article Content - Two Column Grid */}
          <div className="article-content-compact content-two-column">
            
            {/* 01 The Problem */}
            <div className="cs-section">
              <h2 className="cs-section-title">THE PROBLEM</h2>
              
              <p>Leaders needed to assess organizational readiness quickly and report with confidence. But critical data existed scattered across spreadsheets, PDFs, and disconnected tools. The question they couldn't answer: "What's our biggest risk right now?" Manual reports took 2-3 days. By the time insights arrived, conditions had changed. This wasn't a data problem. It was a decision-making problem. Information existed, but not in a form that supported fast, confident decision-making.</p>
            </div>

            {/* 02 My Role */}
            <div className="cs-section">
              <h2 className="cs-section-title">MY ROLE</h2>
              
              <p>Lead product designer, end-to-end: stakeholder interviews with executives and analysts, information architecture and data hierarchy design, interaction patterns for progressive disclosure, visual system for risk communication, and real-time data integration with engineering.</p>
            </div>

            {/* 03 Design Approach & Key Decisions */}
            <div className="cs-section">
              <h2 className="cs-section-title">DESIGN APPROACH</h2>
              
              <h3 className="cs-subsection-headline">Three-tier information hierarchy</h3>
              <p>Mapped the decision-making flow to create: at-a-glance health indicators, filterable views on-demand, and deep-dive detailed reports.</p>

              <h3 className="cs-subsection-headline">Trends over raw numbers</h3>
              <p>Stakeholder feedback revealed absolute numbers didn't tell the story. A system with 10 high-risk items today might be improving (was 20 last month) or deteriorating (was 5 last month). Added trend indicators and comparison views to show direction of change.</p>

              <h3 className="cs-subsection-headline">Progressive disclosure, not information overload</h3>
              <p>Early prototypes tried to surface all available data upfront. User testing showed this created cognitive overload and actually slowed decision-making. Defaulted to summary view with clear pathways to details. Let users choose their depth of exploration.</p>

              <h3 className="cs-subsection-headline">Make vendor dependencies visible</h3>
              <p>Research revealed vendor-related incidents were a growing concern (third-party outages, security breaches), but existing tools didn't make these relationships visible. Created a dedicated vendor exposure view showing which vendors, if disrupted, would impact the most critical systems.</p>
            </div>

            {/* 04 Solution */}
            <div className="cs-section">
              <h2 className="cs-section-title">SOLUTION</h2>
              
              <h3 className="cs-subsection-headline">Risk heat map</h3>
              <p>Visual matrix showing recovery time objectives vs. actual recovery capability across all critical systems. Instantly highlights which systems are at risk.</p>

              <h3 className="cs-subsection-headline">Vendor exposure view</h3>
              <p>Shows dependency on third-party vendors with integrated risk assessment. Helps executives understand concentration risk and single points of failure.</p>

              <h3 className="cs-subsection-headline">Plan coverage analysis</h3>
              <p>Tracks which technologies have documented, tested recovery plans and which don't. Surfaces gaps before they become problems during an incident.</p>

              <h3 className="cs-subsection-headline">Real-time updates</h3>
              <p>Dashboard refreshes automatically with latest data from integrated systems. No more waiting for weekly or monthly static reports.</p>
            </div>

            {/* 05 Impact */}
            <div className="cs-section">
              <h2 className="cs-section-title">IMPACT</h2>
              
              <ul className="cs-list-compact">
                <li>24+ hours/month saved in manual report generation across the BC/DR team</li>
                <li>Real-time insights vs. previous 2-3 day reporting lag</li>
                <li>Faster incident response during actual disruptions</li>
              </ul>
            </div>

            {/* 06 What I Learnt */}
            <div className="cs-section cs-section-last">
              <h2 className="cs-section-title">WHAT I LEARNT</h2>
              
              <p>Enterprise UX isn't about aesthetics. It's about understanding the actual job to be done and eliminating friction in high-stakes workflows.</p>

              <p>Success came from deeply understanding how BC/DR leaders make decisions under pressure, not from adding more features or data points.</p>

              <p>In crisis-oriented domains, the best interface delivers the answer before the question is fully formed. Every pixel should reduce cognitive overhead.</p>

              <p><strong>If I did this again:</strong> I'd spend more time observing users during incident response scenarios. Edge cases and real-world constraints that emerge during crises would inform the design earlier.</p>
            </div>

          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="detail-footer">
        <button onClick={handleBackToPortfolio} className="back-to-portfolio">
          <ArrowLeft size={18} />
          Go back
        </button>
      </footer>
    </div>
  );
};

export default BCDRCaseStudy;
